/**
 * Encryption utilities for generating secure registration links
 * Uses AES-256-GCM encryption with Web Crypto API
 */

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

/**
 * Convert hex string to Uint8Array
 * @param {string} hex - Hex string
 * @returns {Uint8Array}
 */
const hexToBytes = (hex) => {
	const bytes = new Uint8Array(hex.length / 2);
	for (let i = 0; i < hex.length; i += 2) {
		bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
	}
	return bytes;
};

/**
 * Convert Uint8Array to base64 string
 * @param {Uint8Array} bytes
 * @returns {string}
 */
const bytesToBase64 = (bytes) => {
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
};

/**
 * Encrypt account ID using AES-256-GCM
 * @param {string|number} accountId - The account ID to encrypt
 * @returns {Promise<string>} - Encrypted string in format "iv:authTag:ciphertext" (base64 encoded, URL safe)
 */
export const encryptAccountId = async (accountId) => {
	if (!ENCRYPTION_KEY) {
		throw new Error("Encryption key not configured");
	}

	const keyBytes = hexToBytes(ENCRYPTION_KEY);
	const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes for GCM
	const encoder = new TextEncoder();
	const data = encoder.encode(String(accountId));

	// Import the key
	const cryptoKey = await crypto.subtle.importKey(
		"raw",
		keyBytes,
		{ name: "AES-GCM" },
		false,
		["encrypt"]
	);

	// Encrypt the data
	const encryptedBuffer = await crypto.subtle.encrypt(
		{
			name: "AES-GCM",
			iv: iv,
			tagLength: 128, // 16 bytes auth tag
		},
		cryptoKey,
		data
	);

	// The encrypted result includes the ciphertext + auth tag (last 16 bytes)
	const encryptedArray = new Uint8Array(encryptedBuffer);
	const ciphertext = encryptedArray.slice(0, -16);
	const authTag = encryptedArray.slice(-16);

	// Format: iv:authTag:ciphertext (all base64 encoded)
	const ivBase64 = bytesToBase64(iv);
	const authTagBase64 = bytesToBase64(authTag);
	const ciphertextBase64 = bytesToBase64(ciphertext);

	return `${ivBase64}:${authTagBase64}:${ciphertextBase64}`;
};

/**
 * Generate a registration link for a customer
 * @param {string|number} accountId - The account ID
 * @param {string} baseUrl - The base URL for the consumer portal (default: https://paymypayments.com)
 * @returns {Promise<string>} - The full registration URL
 */
export const generateRegistrationLink = async (
	accountId,
	baseUrl = "https://paymypayments.com"
) => {
	const encryptedId = await encryptAccountId(accountId);
	const encodedId = encodeURIComponent(encryptedId);
	return `${baseUrl}/register?accountID=${encodedId}`;
};
